import type { ApiClient } from "../api";
import type { RealtimeConfig } from "../entities/notifications";
import { RealtimeManager } from "./RealtimeManager";

interface RealtimeSubscriptionOptions {
  client: ApiClient;
  /** Настройки соединения приходят с сервера, поэтому загружаются асинхронно. */
  loadConfig: () => Promise<{ data: RealtimeConfig }>;
  /** Вызывается после каждого (пере)подключения: пора перечитать durable-данные. */
  onReconnect: () => void;
  /** Подписывается на каналы и возвращает функцию отписки. */
  subscribe: (manager: RealtimeManager) => () => void;
}

/**
 * Открывает realtime-соединение на время жизни React-эффекта и возвращает
 * функцию, которую эффект отдаёт как cleanup.
 *
 * Отвечает за то, что раньше каждый экран писал вручную: загрузить настройки,
 * создать `RealtimeManager`, подписаться и на выходе отписаться и закрыть
 * сокет. Главное — корректная отмена: если эффект завершился раньше, чем
 * пришли настройки (человек ушёл с экрана, сменился документ), соединение
 * вообще не открывается. Без этой проверки сокет создавался уже после cleanup
 * и оставался висеть до закрытия вкладки.
 *
 * Используется везде, где компонент слушает realtime-каналы. Ошибки загрузки
 * настроек и подписки намеренно молчаливы: realtime лишь подсказка, данные
 * по-прежнему обновляются обычными запросами.
 */
export function openRealtimeSubscription({
  client,
  loadConfig,
  onReconnect,
  subscribe,
}: RealtimeSubscriptionOptions): () => void {
  let cancelled = false;
  let manager: RealtimeManager | undefined;
  let leave: (() => void) | undefined;

  void loadConfig()
    .then(({ data }) => {
      if (cancelled) return;
      manager = new RealtimeManager(client);
      manager.connect(data, onReconnect);
      leave = subscribe(manager);
    })
    .catch(() => undefined);

  return () => {
    cancelled = true;
    leave?.();
    manager?.disconnect();
  };
}
